<template>
  <div class="vacation-type-modal" @keydown.esc="close">
    <remove-type-modal ref="removeTypeModal" @remove="removeType" />

    <c-sidesheet :active="active" :title="id ? 'Редактирование типа отпуска' : 'Новый тип отпуска'" @close="close">
      <template #actions>
        <div id="vacation-type-delete-button-wrapper">
          <c-button-2
            v-if="id"
            :disabled="!canDelete"
            :icon="{ icon: 'trash', color: 'secondary' }"
            class="vacation-type-form__save"
            label="Удалить"
            size="small"
            @click="remove"
          />
        </div>
      </template>

      <b-popover
        v-if="!canDelete"
        placement="top"
        container="app"
        boundary="window"
        :target="'vacation-type-delete-button-wrapper'"
        triggers="hover blur"
      >
        <error-feedback
          class="m-0 empty-category-tooltip"
          text='В данный момент удаление невозможно, так как тип отпуска используется в разделе "Графики отпусков и больничных"'
        />
      </b-popover>

      <div class="vacation-type-form">
        <div class="vacation-type-form__form">
          <div class="vacation-type-form__name">
            <c-input-2
              v-model="form.name"
              floating-label="Наименование"
              :required="nameError"
              :required-condition="nameError"
              :required-message="nameError"
              :required-mark="true"
              :disabled="isSick"
              :clearable="true"
              :max-length="500"
            ></c-input-2>
          </div>

          <div class="vacation-type-form__description">
            <c-textarea
              ref="description"
              v-model="form.description"
              floating-label="Описание"
              :clearable="true"
              :max-length="500"
            ></c-textarea>
          </div>

          <div class="vacation-type-form__row">
            <div class="vacation-type-form__color">
              <color-select
                ref="color"
                v-model="form.color"
                :validate="submitted"
                default-color="#DFE4EF"
                :disabled="isSick"
                label="Цвет в графике отпусков"
              ></color-select>
            </div>
            <div class="vacation-type-form__code">
              <c-input-2
                v-model="form.code"
                floating-label="Код для табеля"
                :clearable="true"
                :max-length="2"
              ></c-input-2>
            </div>
          </div>
        </div>

        <div class="vacation-type-form__actions">
          <c-button-2 size="large" class="vacation-type-form__reset" label="Отменить" @click="close" />

          <c-button-2
            class="vacation-type-form__save"
            label="Сохранить"
            color="primary"
            size="large"
            :disabled="pending"
            @click="submit"
          />
        </div>
      </div>
    </c-sidesheet>
  </div>
</template>

<script>
import Sidesheet from "@/components/sidesheet";
import Input2 from "@/components/controls2/Input";
import CTextarea from "@/components/textarea";
import Button2 from "@/components/controls2/Button";
import ColorSelect from "@/components/controls/color-select";
import RemoveTypeModal from "../remove-type-modal";
import ErrorFeedback from "@/components/error-feedback";

export default {
  name: "vacation-type-sidesheet",

  components: {
    "c-sidesheet": Sidesheet,
    "c-input-2": Input2,
    "c-button-2": Button2,
    "color-select": ColorSelect,
    CTextarea,
    RemoveTypeModal,
    ErrorFeedback,
  },

  data() {
    return {
      type: null,
      id: null,
      active: false,

      submitted: false,
      pending: false,

      isSick: false,
      canDelete: false,

      form: {
        name: "",
        description: "",
        color: "",
        code: "",
      },

      serverErrors: {
        name: "",
      },
    };
  },

  computed: {
    nameError() {
      if (!this.submitted) return false;
      if (!this.form.name) return "Обязательное поле";
      return this.serverErrors.name;
    },
    isValid() {
      return !this.nameError && this.$refs.color.isValid;
    },
  },

  watch: {
    "form.name": function () {
      this.serverErrors.name = "";
    },
  },

  created() {
    document.getElementsByTagName("body")[0].addEventListener("click", () => {
      this.$root.$emit("bv::hide::popover");
    });

    document.addEventListener("keyup", (evt) => {
      if (evt.keyCode === 27) {
        if (this.active) {
          this.close();
        }
      }
    });
  },

  methods: {
    reset() {
      this.submitted = false;
      this.type = null;
      this.id = null;
      this.form.name = "";
      this.form.description = "";
      this.form.color = "";
      this.form.code = "";
      this.isSick = false;
    },

    open(params) {
      this.reset();
      if (params && params.vacationType) {
        this.type = params.vacationType;
        this.isSick = params.vacationType.is_sick;
        this.canDelete = params.vacationType.canDelete;
        this.id = params.vacationType.id;
        this.form.name = params.vacationType.name;
        this.form.description = params.vacationType.description;
        this.form.color = params.vacationType.color;
        this.form.code = params.vacationType.timesheet_code;
      }
      this.active = true;
    },

    close() {
      this.active = false;

      this.$emit("close");
    },

    remove() {
      this.$refs.removeTypeModal.open({
        type: this.type,
      });
    },

    removeType() {
      this.close();
      this.$nextTick(() => {
        this.$emit("remove", { type: this.type });
      });
    },

    getParams() {
      return {
        id: this.id,
        name: this.form.name,
        description: this.form.description,
        color: this.form.color,
        code: this.form.code,
      };
    },

    submit() {
      this.submitted = true;

      if (!this.isValid) return;

      const action = this.id ? "referenceVacationTypes/editType" : "referenceVacationTypes/addType";

      this.$store.dispatch(action, this.getParams()).then((res) => {
        if (res.success) {
          this.close();
        } else {
          const { errors } = res;
          if (errors.name) {
            const [firstError] = errors.name;
            this.serverErrors.name = firstError;
          }
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

::v-deep .sidesheet__header {
  @media screen and (max-width: 700px) {
    display: block;
  }
}

::v-deep .sidesheet__wrapper {
  width: calc(100vw - 161px);
  padding: 38px 14px 38px 31px;

  @media screen and (max-width: 1024px) {
    width: calc(100vw - 62px);
  }

  @include mobileAndTablet {
    padding-right: 30px;
  }

  @media screen and (max-width: 700px) {
    padding: 12px 0px 0px;
    max-width: 100%;
    width: 100%;
  }
}

::v-deep .sidesheet__content {
  @media screen and (max-width: 700px) {
    display: flex;
    flex-direction: column;
  }
}

::v-deep .sidesheet__title {
  @media screen and (max-width: 700px) {
    font-size: 15px;
  }
}

::v-deep .sidesheet__close {
  @media screen and (max-width: 700px) {
    margin-bottom: 0;
  }
}

::v-deep .sidesheet__actions {
  @media screen and (max-width: 700px) {
    margin-top: 12px;
  }
}

.vacation-type-form {
  margin-top: 32px;

  &__name {
    margin-bottom: 42px;
  }

  &__name,
  &__description {
    max-width: 565px;
  }

  &__color {
    & /deep/ .select-color__label {
      margin-bottom: 6px;
    }
    & /deep/ .select-color__icon {
      display: none;
    }
  }

  &__code {
    margin-left: 30px;
    width: 168px;
    margin-top: 1px;
  }

  &__row {
    display: flex;
  }

  @include mobileAndTablet {
    &__name,
    &__description {
      max-width: none;
    }
  }

  @include onlyStandartMobile {
    &__color {
      & /deep/ {
        .cp {
          flex-grow: 1;

          &-input {
            flex-grow: 1;
          }
        }
      }
    }
  }

  &__description {
    margin-bottom: 24px;
  }

  &__actions {
    margin-top: 30px;
    display: flex;
    align-items: center;
  }

  &__reset {
    margin-right: 10px;
  }

  @media screen and (max-width: 700px) {
    height: 100%;
    display: flex;
    flex-direction: column;
    margin-top: 24px;

    &__row {
      flex-wrap: wrap;
    }

    &__color {
      width: 100%;
      margin-bottom: 24px;
    }
    &__code {
      margin-left: 0;

      width: 100%;
    }

    &__actions {
      margin-top: auto;
      margin-left: -20px;
      margin-right: -20px;

      .c-button {
        border-radius: 0 !important;
        width: 50%;
        margin: 0 !important;
      }
    }
  }
}
</style>
